import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import {
  DOUBT_BUDDY_SOCKET_GENERIC_ERROR,
  isDoubtBuddySocketErrorChunk,
  isDoubtBuddySocketErrorMarkdown,
} from "../lib/doubtBuddySocketError";

export type RevisePhase =
  | "idle"
  | "connecting"
  | "processing"
  | "streaming"
  | "complete"
  | "error";

export type StartReviseParams = {
  questionText: string;
  imageUrl?: string;
};

export interface UseDoubtBuddyReviseReturn {
  phase: RevisePhase;
  finalMarkdown: string | null;
  error: string | null;
  startRevise: (params: StartReviseParams) => void;
  disconnect: () => void;
}

const envVars =
  typeof import.meta !== "undefined"
    ? (import.meta as unknown as { env?: Record<string, string> }).env
    : undefined;

const SOCKET_URL =
  envVars?.VITE_DOUBT_BUDDY_SOCKET_URL || "https://socketapi.doubtbuddy.com";

const DOUBT_BUDDY_API_KEY = envVars?.VITE_DOUBT_BUDDY_API_KEY || "";

const IS_LOCAL_DEV =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

function assembledChunks(chunks: string[]): string {
  return chunks.join("");
}

export function useDoubtBuddyRevise(): UseDoubtBuddyReviseReturn {
  const [phase, setPhase] = useState<RevisePhase>("idle");
  const [finalMarkdown, setFinalMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const chunksRef = useRef<string[]>([]);
  const questionTextRef = useRef<string>("");
  const imageUrlRef = useRef<string | null>(null);
  const streamingStartedRef = useRef(false);
  const solutionEndMarkdownRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    const s = socketRef.current;
    if (s) {
      s.off();
      s.disconnect();
      socketRef.current = null;
    }
  }, []);

  const failWithGenericError = useCallback(() => {
    setError(DOUBT_BUDDY_SOCKET_GENERIC_ERROR);
    setPhase("error");
    cleanup();
  }, [cleanup]);

  useEffect(() => cleanup, [cleanup]);

  const disconnect = useCallback(() => {
    cleanup();
    setPhase("idle");
  }, [cleanup]);

  const startRevise = useCallback(
    (params: StartReviseParams) => {
      cleanup();

      setPhase("connecting");
      setFinalMarkdown(null);
      setError(null);
      chunksRef.current = [];
      questionTextRef.current = params.questionText;
      imageUrlRef.current = params.imageUrl?.trim() ? params.imageUrl : null;
      streamingStartedRef.current = false;
      solutionEndMarkdownRef.current = null;

      const socket = io(IS_LOCAL_DEV ? window.location.origin : SOCKET_URL, {
        transports: ["polling"],
        ...(IS_LOCAL_DEV ? {} : { withCredentials: true }),
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        socket.emit("login", { apiKey: DOUBT_BUDDY_API_KEY });
      });

      socket.on("connect_error", (err: Error) => {
        setError(err?.message ?? "Connection failed");
        setPhase("error");
        cleanup();
      });

      socket.on("login-success", () => {
        setPhase("processing");
        const active = socketRef.current;
        if (!active) return;

        const imageUrl = imageUrlRef.current;
        if (imageUrl) {
          void (async () => {
            try {
              const res = await fetch(imageUrl, { mode: "cors" });
              if (!res.ok) {
                throw new Error(`Image fetch failed (${res.status})`);
              }
              const blob = await res.blob();
              let fileName = "filename.jpg";
              try {
                const path = new URL(imageUrl, window.location.href).pathname;
                const last = path.split("/").pop();
                if (last && /\.(jpe?g|png|gif|webp)$/i.test(last)) {
                  fileName = last;
                }
              } catch {
                /* keep default */
              }
              active.emit("image-query", {
                fileContent: blob,
                fileName,
              });
            } catch {
              setError("Could not load the question image. Please try again.");
              setPhase("error");
              cleanup();
            }
          })();
        } else {
          active.emit("text-query", {
            textQuestion: questionTextRef.current,
          });
        }
      });

      socket.on("processing-status", () => {
        /* Android logs only; no state change */
      });

      socket.on("query-update-solution", (data: { chunk?: string }) => {
        if (!data?.chunk) return;
        if (isDoubtBuddySocketErrorChunk(data.chunk)) {
          failWithGenericError();
          return;
        }
        chunksRef.current.push(data.chunk);
        if (!streamingStartedRef.current) {
          streamingStartedRef.current = true;
          setPhase("streaming");
        }
      });

      socket.on("query-response-solution", () => {
        /* Android logs only; no state change */
      });

      socket.on("solution-end", (markdown: string) => {
        const fromEvent =
          typeof markdown === "string" && markdown.trim() ? markdown : "";
        const assembled = fromEvent || assembledChunks(chunksRef.current);
        if (isDoubtBuddySocketErrorMarkdown(assembled)) {
          failWithGenericError();
          return;
        }
        if (assembled.trim()) {
          solutionEndMarkdownRef.current = assembled;
          setFinalMarkdown(assembled);
        }
      });

      socket.on("final-solution", () => {
        const assembled =
          solutionEndMarkdownRef.current?.trim() ||
          assembledChunks(chunksRef.current);
        if (!assembled.trim() || isDoubtBuddySocketErrorMarkdown(assembled)) {
          failWithGenericError();
          return;
        }
        setFinalMarkdown(assembled);
        setPhase("complete");
        cleanup();
      });

      socket.on("doubt-end", (data: { error?: boolean }) => {
        if (!data?.error) return;
        const assembled = assembledChunks(chunksRef.current);
        if (assembled.trim() && !isDoubtBuddySocketErrorMarkdown(assembled)) {
          setFinalMarkdown(assembled);
          setPhase("complete");
        } else {
          failWithGenericError();
        }
        cleanup();
      });

      socket.on("error", () => {
        failWithGenericError();
      });

      socket.on("disconnect", () => {
        socketRef.current = null;
      });
    },
    [cleanup, failWithGenericError],
  );

  return { phase, finalMarkdown, error, startRevise, disconnect };
}
